import { SUB_CATEGORIES } from '../../types/categories';
import { getVoCalculator, isValidCalculator, isVoiceOverCalculator } from '../calculator';
import { translate } from '../translation';
import {
    CUSTOM_EXTRA_TYPE,
    EXTRA_FAST_TYPE,
    FEATURE_TYPE,
    SCRIPT_PROOFREADING_FEATURE,
    UNLIMITED_REVISION,
    UNLIMITED_VALUES
} from './constants';

export const calcCurrentPrice = (packageData, quantity = 1) => {
    const { calculators, extraFast, features = [], customExtras = [] } = packageData;
    let currentPrice = packageData.price;

    calculators.forEach((calculator) => {
        if (isValidCalculator(calculator)) {
            const { value, currentValue, price, additionalValue } = calculator;
            if (isVoiceOverCalculator(calculator)) {
                currentPrice = calculator.price;
            } else {
                // use Number for the case the currentValue is an empty string
                const delta = Number(currentValue) - value;
                currentPrice += Math.ceil(delta / additionalValue) * price;
            }
        }
    });

    if (isExtraFastIncluded(extraFast)) {
        currentPrice += extraFast.price;
    }

    features.filter(isPricingFactorIncluded).forEach((feature) => currentPrice += feature.price * feature.amount);
    customExtras.filter(isPricingFactorIncluded).forEach((customExtra) => currentPrice += customExtra.price);

    return currentPrice * quantity;
};

export const calcCurrentDuration = (packageData) => {
    const { calculator, extraFast, duration, features, customExtras } = packageData;
    let currentDuration = duration;

    // If it's a VO calculator currentDuration will be calculated when VoData is calculated as part of that action
    if (isExtraFastIncluded(extraFast) && !isVoiceOverCalculator(calculator)) {
        currentDuration = extraFast.currentDuration;
    }

    features.filter(isPricingFactorIncluded).forEach((feature) => currentDuration += feature.currentDuration);
    customExtras.filter(isPricingFactorIncluded).forEach((customExtra) => currentDuration += customExtra.duration);

    return currentDuration;
};

export const formatDaysKey = (days) => days === 1 ? 'gig_page_perseus.general.day' : 'gig_page_perseus.general.days';

export const formatPricingFactorValue = (value) => isUnlimited(value) ? UNLIMITED_REVISION : value;

export const isUnlimited = (value) => UNLIMITED_VALUES.includes(value);

export const getFixedDropdownArr = () => (Array.from({ length: 5 }, (v, k) => k + 1));

export const getValidExtras = ({ features = [], customExtras = [], extraFast }) => {
    let validExtras = [];

    if (extraFast && extraFast.currentDuration && extraFast.currentPrice) {
        validExtras.push({
            id: extraFast.id,
            name: extraFast.name,
            currentPrice: extraFast.currentPrice,
            amount: extraFast.currentValue,
            hasQuantity: false,
            label: translate('gig_page_perseus.packages.extra_fast', {
                amount: extraFast.currentDuration
            }),
            type: EXTRA_FAST_TYPE
        });
    }

    validExtras = validExtras.concat(features
        .reduce((acc, feature) => {
            if (!feature.included && feature.label && feature.currentPrice) {
                const extendedFeature = {
                    ...feature,
                    type: FEATURE_TYPE,
                    id: feature.id,
                    currentPrice: feature.currentPrice
                };
                if (feature.name === SCRIPT_PROOFREADING_FEATURE) {
                    extendedFeature.hasQuantity = false;
                    acc.unshift(extendedFeature);
                } else {
                    acc.push(extendedFeature);
                }
            }

            return acc;
        }, []));

    validExtras = validExtras.concat(customExtras.reduce((acc, customExtra) => {
        if (customExtra.label && customExtra.price) {
            acc.push({
                ...customExtra,
                type: CUSTOM_EXTRA_TYPE,
                id: customExtra.id,
                currentPrice: customExtra.price,
                hasQuantity: false
            });
        }

        return acc;
    }, []));

    return validExtras;
};

export const getPackageTypeById = (id) => {
    switch (id) {
        case 1:
            return 'gig_page_perseus.packages.basic';
        case 2:
            return 'gig_page_perseus.packages.standard';
        case 3:
            return 'gig_page_perseus.packages.premium';
        case 4:
            return 'gig_page_perseus.packages.custom';
        default:
            return '';
    }
};

export const isVoiceOver = ({ subCategoryId }) => SUB_CATEGORIES.VOICE_OVERS === subCategoryId;

export const isPricingFactorIncluded = (pricingFactor) => pricingFactor && pricingFactor.amount > 0;

export const isExtraFastIncluded = (extraFast) => extraFast && extraFast.currentValue === 1;

export const isRecommendedPackage = (packageData) => !!packageData.recommendedType;

export const hasRecommendedPackage = (packageList) =>
    packageList.some((packageData) => isRecommendedPackage(packageData));

export const isValidVoPackage = (calculators, subCategoryId) => !!getVoCalculator(calculators) && isVoiceOver({ subCategoryId });
